import { Button, Flex, Tooltip } from "@radix-ui/themes";
import { useParams } from "react-router-dom";
import { PlusIcon, MinusIcon } from "@radix-ui/react-icons";

import { useAppSelector, useAppDispatch } from "@/app/appStore";
import {
  selectAnnotationData,
  selectCurrentCamera,
  selectAnnotationView,
  selectStoreFavoriteCameras,
  addFavoriteCamera,
  removeFavoriteCamera,
} from "@/entities/annotation";

import { ANNOTATION_TYPES } from "@/entities/annotation/model/consts";
import useCameraHandlers from "../hooks/useCameraHandlers";
import { getDefaultCamerasForStore } from "../lib/helpers";

const FavoriteCameras = () => {
  const { storeKey = "" } = useParams<{ storeKey: string }>();
  const defaultCameras = getDefaultCamerasForStore(storeKey);
  const dispatch = useAppDispatch();
  const favoriteCameras = useAppSelector((state) =>
    selectStoreFavoriteCameras(state, storeKey)
  );
  const selectedCamera = useAppSelector(selectCurrentCamera);
  const annotationView = useAppSelector(selectAnnotationView);
  const annotationData = useAppSelector(selectAnnotationData);

  const isFavoriteCamera = favoriteCameras.includes(selectedCamera);
  const isDefaultCamera = defaultCameras.includes(selectedCamera);

  const isProximityTimeRangesAnnotation =
    annotationView === ANNOTATION_TYPES.PROXIMITY_TIME_RANGES &&
    !!annotationData;
  const { handleDefaultCameraSelect, cameraData } = useCameraHandlers();

  const handleAddCamera = () => {
    if (selectedCamera) {
      dispatch(addFavoriteCamera({ storeKey, cameraId: selectedCamera }));
    }
  };

  const handleRemoveCamera = () => {
    if (selectedCamera) {
      dispatch(removeFavoriteCamera({ storeKey, cameraId: selectedCamera }));
    }
  };

  const renderCameraButton = (cameraId: string) => (
    <Button
      key={cameraId}
      size="1"
      color="gray"
      variant={cameraId === selectedCamera ? "soft" : "ghost"}
      className="m-0"
      disabled={
        !cameraData.allCameras?.some((camera) => camera.id === cameraId) ||
        isProximityTimeRangesAnnotation
      }
      onClick={() => handleDefaultCameraSelect(cameraId)}
    >
      {cameraId}
    </Button>
  );

  return (
    <>
      {favoriteCameras.length > 0 && (
        <Flex gap="1" align="center" className="flex-wrap sm:flex-nowrap">
          {favoriteCameras.map(renderCameraButton)}
        </Flex>
      )}

      <Tooltip
        content="Add active camera to your favorites"
        width="130px"
        className="text-center"
      >
        <Button
          size="1"
          color="gray"
          variant="soft"
          className="p-1 flex-shrink-0"
          disabled={!selectedCamera || isFavoriteCamera || isDefaultCamera}
          onClick={handleAddCamera}
        >
          <PlusIcon />
        </Button>
      </Tooltip>

      <Tooltip
        content="Remove active camera from your favorites"
        width="150px"
        className="text-center"
      >
        <Button
          size="1"
          color="gray"
          variant="soft"
          className="p-1 flex-shrink-0"
          disabled={!isFavoriteCamera}
          onClick={handleRemoveCamera}
        >
          <MinusIcon />
        </Button>
      </Tooltip>
    </>
  );
};

export default FavoriteCameras;
